import React, { useState } from 'react';
import { browserLocalPersistence, browserSessionPersistence, getAuth, setPersistence, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthPageLayout from './AuthPageLayout';

const friendlyLoginError = (error) => {
  if (['auth/invalid-credential', 'auth/user-not-found', 'auth/wrong-password'].includes(error.code)) return 'That email and password combination does not match an account.';
  if (error.code === 'auth/too-many-requests') return 'Too many attempts. Wait a moment, then try again.';
  if (error.code === 'auth/invalid-email') return 'Enter a valid email address.';
  return 'We could not sign you in. Please try again.';
};

function LoginPage() {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loginStarted, setLoginStarted] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  if (currentUser && !loginStarted) return <Navigate to="/" />;

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    setLoginStarted(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;
      let profileSnapshot = await getDoc(doc(db, 'users', user.uid));
      let userId;
      if (profileSnapshot.exists()) {
        userId = profileSnapshot.data().userId;
      } else {
        const fallbackSnapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', user.uid)));
        if (!fallbackSnapshot.empty) userId = fallbackSnapshot.docs[0].data().userId;
      }
      navigate(userId ? `/UserProfile/${userId}` : '/');
    } catch (loginError) {
      console.error('Login failed:', loginError);
      setError(friendlyLoginError(loginError));
    } finally {
      setSubmitting(false);
      setLoginStarted(false);
    }
  };

  return (
    <AuthPageLayout
      mode="login"
      eyebrow="RETURN TO BASECAMP"
      title="Welcome back"
      description="Sign in to reconnect with your team and keep planning what comes next."
    >
      <form className="auth-form" onSubmit={handleLogin}>
        {error && <div className="auth-error" role="alert">{error}</div>}
        <label className="auth-field"><span>Email</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" autoComplete="email" required /></label>
        <label className="auth-field"><span>Password</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Your password" autoComplete="current-password" required /></label>
        <div className="auth-form-options"><label><input type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} /> Keep me signed in</label><span>Secure Firebase sign-in</span></div>
        <button className="auth-submit" type="submit" disabled={submitting}>{submitting ? 'Signing in…' : 'Sign in to ClimberLink'}</button>
      </form>
      <div className="auth-separator"><span>NEW TO THE NETWORK?</span></div>
      <Link className="auth-secondary-action" to="/signup">Create a climbing profile</Link>
    </AuthPageLayout>
  );
}

export default LoginPage;
